import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import Pantalla404 from '@/components/paginas/Compartidas/404';
import { useAppSelector } from '@/hooks/redux';
import PlantillaCredenciales from '@/components/plantilla/PlantillaCredenciales';
import PersonaLogin from '@/assets/img/persona_login.png';
import PersonaRegister from '@/assets/img/persona_register.png';
import PersonaReset from '@/assets/img/persona_reset.png';
import PlantillaFactoring from '@/components/plantilla/PlantillaFactoring';
import VerificacionEjecutivo from '@/components/paginas/Credenciales/Verificacion/VerificacionEjecutivo';
import PlantillaAvisosLegales from '@/components/plantilla/PlantillaAvisosLegales';
import Politicas from '@/components/paginas/AvisosLegales/Politicas';
import Terminos from '@/components/paginas/AvisosLegales/Terminos';
import TerminosUso from '@/components/paginas/AvisosLegales/TerminosUso';
import {
  CLIENTE,
  EJECUTIVO_ADMIN,
  EJECUTIVO_VISOR,
} from '@/redux/slices/usuarioSlice';
import ReiniciarScroll from './ReiniciarScroll';
import InicioCliente from '@/components/paginas/Cliente/Inicio';
import env from '@/providers/env';
import ExternalRedirect from '@/components/paginas/Compartidas/ExternalRedirect';
import CargandoPantallaCompleta from '@/components/plantilla/CargandoPantallaCompleta';

//#region CREDENCIALES
export const CREDENCIALES = {
  INICIARSESION: '/iniciar-sesion',
  REGISTRO: '/registro',
  // Verificacion de cliente
  VERIFICACION: '/verificar',
  // Verificacion de ejecutivo para new factor
  NEWFACTOR: '/newfactor',

  // Olvide mi contraseña
  RECUPERARCONTRASEÑA: '/recuperar-contraseña', // Paso 1
  RESTABLECER: '/restablecer', // Paso 2
};

const Login = lazy(() => import('@/components/paginas/Credenciales/Login'));
const Registro = lazy(
  () => import('@/components/paginas/Credenciales/Registro')
);
// Recuperar contraseña
const RestablecerPaso1 = lazy(
  () => import('@/components/paginas/Credenciales/Restablecer/RestablecerPaso1')
);
const RestablecerPaso2 = lazy(
  () => import('@/components/paginas/Credenciales/Restablecer/RestablecerPaso2')
);
//
const VerificacionCliente = lazy(
  () =>
    import('@/components/paginas/Credenciales/Verificacion/VerificacionCliente')
);

//#endregion

//#region CLIENTE
export const CLIENTES_FACTORING = {
  INICIO: '/inicio',
  OPERACION: '/operacion',
  MANTENEDOR_CUENTAS: '/cuentas',
  MANTENEDOR_PERFIL: '/perfil',
};

const OperacionesCliente = lazy(
  () => import('../components/paginas/Cliente/Operaciones')
);
const Operacion = lazy(() => import('../components/paginas/Cliente/Operacion'));
const MantenedorCuentas = lazy(
  () => import('@/components/paginas/Cliente/MantenedorCuentas')
);
const MantenedorPerfil = lazy(
  () => import('@/components/paginas/Cliente/MantenedorPerfil')
);

//#endregion

//#region EJECUTIVO
export const EJECUTIVOS_FACTORING = {
  ASIGNACION_EJECUTIVO: 'asignacion-ejecutivo',
  CARPETA_CLIENTES: 'carpeta-clientes',
};

const OperacionesEjecutivo = lazy(
  () => import('../components/paginas/Ejecutivo/Operaciones')
);

const AsignacionEjecutivo = lazy(
  () => import('../components/paginas/Ejecutivo/AsignacionEjecutivo')
);

const CarpetaClientes = lazy(
  () => import('../components/paginas/Ejecutivo/CarpetaClientes/index')
);
//#endregion

//#region COMPARTIDO
export const FACTORING = {
  OPERACIONES: '/operaciones',
  DETALLE_OPERACION: '/operacion/:nro',
  LANDING: '/',
};

export const AVISOS: Record<string, string> = {
  TERMINOS: env.LANDING + 'terminos-y-condiciones',
  POLITICAS: env.LANDING + 'politicas-de-privacidad',
  TERMINOS_USO: env.LANDING + 'terminos-de-uso',
};

const DetalleOperacion = lazy(
  () => import('../components/paginas/Compartidas/DetalleOperacion')
);
//#endregion

const AppRoutes = () => {
  const { idRol } = useAppSelector((state) => state.usuario);

  return (
    <BrowserRouter>
      <ReiniciarScroll>
        <Suspense fallback={<CargandoPantallaCompleta />}>
          <Routes>
            {/* Ruta Fallback */}
            <Route path='*' element={<Pantalla404 />} />

            <Route index element={<ExternalRedirect to={env.LANDING} />} />
            <Route element={<PlantillaAvisosLegales />}>
              <Route path={AVISOS.POLITICAS} element={<Politicas />} />
              <Route path={AVISOS.TERMINOS} element={<Terminos />} />
              <Route path={AVISOS.TERMINOS_USO} element={<TerminosUso />} />
            </Route>

            {/* Rutas Credenciales */}
            <Route
              element={
                <PlantillaCredenciales
                  imageMap={{
                    [CREDENCIALES.INICIARSESION]: PersonaLogin,
                    [CREDENCIALES.REGISTRO]: PersonaRegister,
                    [CREDENCIALES.RECUPERARCONTRASEÑA]: PersonaReset,
                  }}
                  fondoOscuro={{
                    [CREDENCIALES.INICIARSESION]: true,
                    [CREDENCIALES.REGISTRO]: false,
                    [CREDENCIALES.RECUPERARCONTRASEÑA]: true,
                  }}
                />
              }
            >
              <Route path={CREDENCIALES.INICIARSESION} element={<Login />} />
              <Route path={CREDENCIALES.REGISTRO} element={<Registro />} />
              <Route
                path={CREDENCIALES.RECUPERARCONTRASEÑA}
                element={<RestablecerPaso1 />}
              />
            </Route>
            <Route
              path={CREDENCIALES.VERIFICACION}
              element={<VerificacionCliente />}
            />
            <Route
              path={CREDENCIALES.NEWFACTOR}
              element={<VerificacionEjecutivo />}
            />
            <Route
              path={CREDENCIALES.RESTABLECER}
              element={<RestablecerPaso2 />}
            />

            <Route element={<PlantillaFactoring />}>
              {idRol === CLIENTE && (
                <>
                  <Route
                    path={CLIENTES_FACTORING.INICIO}
                    element={<InicioCliente />}
                  />
                  <Route
                    path={FACTORING.OPERACIONES}
                    element={<OperacionesCliente />}
                  />
                  <Route
                    path={CLIENTES_FACTORING.OPERACION}
                    element={<Operacion />}
                  />
                  <Route
                    path={FACTORING.DETALLE_OPERACION}
                    element={<DetalleOperacion />}
                  />
                  <Route
                    path={CLIENTES_FACTORING.MANTENEDOR_CUENTAS}
                    element={<MantenedorCuentas />}
                  />
                  <Route
                    path={CLIENTES_FACTORING.MANTENEDOR_PERFIL}
                    element={<MantenedorPerfil />}
                  />
                </>
              )}
              {(idRol === EJECUTIVO_ADMIN || idRol === EJECUTIVO_VISOR) && (
                <>
                  <Route
                    path={FACTORING.OPERACIONES}
                    element={<OperacionesEjecutivo />}
                  />
                  <Route
                    path={EJECUTIVOS_FACTORING.ASIGNACION_EJECUTIVO}
                    element={<AsignacionEjecutivo />}
                  />
                  <Route
                    path={FACTORING.DETALLE_OPERACION}
                    element={<DetalleOperacion />}
                  />
                  <Route
                    path={EJECUTIVOS_FACTORING.CARPETA_CLIENTES}
                    element={<CarpetaClientes />}
                  />
                </>
              )}
            </Route>
          </Routes>
        </Suspense>
      </ReiniciarScroll>
    </BrowserRouter>
  );
};

export default AppRoutes;
